import React, { useRef } from 'react'
import { LineGraph } from 'blocks.simple/chartGraphs/Line'
import { BarGraph } from 'blocks.simple/chartGraphs/Bar'
import { DoughnutGraph } from '../../../blocks.simple/chartGraphs/Doughunt'
//import { reportMock } from './mockData'
import { useEffect, useState } from 'react'
import moment from 'moment'
import styles from './report_chart.jcss'
import { cn } from 'ethcss'
import { Steps } from '../../../atoms/Steps/Steps-view'
import translate from '../../../core/translate'
import Icon from '../../../blocks.simple/icon/icon'
import { api } from '../../../core/api/ConnectionManager'

interface IReportChartProps {
    selectedInfo: { id: number }
    params: string[]
    itemsType: string
}

interface IReportDataPoint {
    date: moment.MomentInput
    [key: string]: unknown
}

interface IReportContentItem {
    title: string
    dataList: IReportDataPoint[]
}

interface IReportDigitalSignage {
    id: number
    name: string
    content: IReportContentItem[]
}

interface IReportPreview {
    start: moment.MomentInput
    end: moment.MomentInput
    digitalSignages: IReportDigitalSignage[]
}

interface IGraphDataset {
    label: string
    data: Array<string | number>
    borderColor: string | string[]
    backgroundColor: string | string[]
    borderWidth: number
}

interface IGraphData {
    labels: string[]
    datasets: IGraphDataset[]
}

interface IReportItemTab {
    id: number
    name: string
}

function ReportChart({ selectedInfo, params, itemsType }: IReportChartProps) {
    const [report, setReport] = useState<IReportPreview | null>(null)
    const [graphType, setGraphType] = useState<string>('lines')
    const [contentKey, setContentKey] = useState<string>(params.length ? params[0] : '')
    const [selectedItem, setSelectedItem] = useState<number | null>(null)
    const [graphData, setGraphData] = useState<IGraphData | null>(null)
    const [doughnutGraphData, setDoughnutGraphData] = useState<IGraphData | null>(null)
    const [isMobile, setIsMobile] = useState<boolean>(false)
    const [hideLeftPanel, setHideLeftPanel] = useState<boolean>(true)
    const [hideRightPanel, setHideRightPanel] = useState<boolean>(true)
    const graphTypes = ['lines', 'bars', 'doughnuts']
    const wrapperRef = useRef<HTMLDivElement | null>(null)

    const checkAvailableItems = () => {
        return Boolean(report?.digitalSignages.length)
    }

    const getItemsTabs = (items: IReportDigitalSignage[]): IReportItemTab[] => {
        return items.map((item) => {
            return { id: item.id, name: item.name }
        })
    }
    const getGraphValue = (dataPoint: IReportDataPoint | undefined): string | number => {
        const value = dataPoint?.[contentKey]

        return typeof value === 'string' || typeof value === 'number' ? value : 0
    }
    const convertDataToGraph = (selectedItem: number | null) => {
        if (report === null) return

        const digitalSignage = report.digitalSignages.find((item) => item.id === selectedItem)

        if (!digitalSignage) return

        const startDate = moment(report.start)
        const endData = moment(report.end)
        const daysCount = endData.diff(startDate, 'days')
        const labels = [startDate.format('DD.MM.YYYY')]
        const content = digitalSignage.content
        const datasets: IGraphDataset[] = []

        for (let i = 0; i < daysCount; i++) {
            labels.push(startDate.add(1, 'days').format('DD.MM.YYYY'))
        }

        content.forEach((element) => {
            datasets.push({
                label: element.title,
                data: labels.map((label) => {
                    const data = element.dataList.find((item) => moment(item.date).format('DD.MM.YYYY') === label)

                    return getGraphValue(data)
                }),
                borderColor: getRandomColor(),
                backgroundColor: getRandomColor(),
                borderWidth: 1,
            })
        })

        setGraphData({
            labels,
            datasets,
        })
    }

    const convertDataToDoughnutGraph = (selectedItem: number | null) => {
        if (report === null) return

        const digitalSignage = report.digitalSignages.find((item) => item.id === selectedItem)

        if (!digitalSignage) return

        const startDate = moment(report.start)
        const endData = moment(report.end)
        const daysCount = endData.diff(startDate, 'days')
        const labels = [
            {
                date: startDate.format('DD.MM.YYYY'),
                borderColor: getRandomColor(),
                backgroundColor: getRandomColor(),
            },
        ]
        const content = digitalSignage.content
        const datasets: IGraphDataset[] = []

        for (let i = 0; i < daysCount; i++) {
            labels.push({
                date: startDate.add(1, 'days').format('DD.MM.YYYY'),
                borderColor: getRandomColor(),
                backgroundColor: getRandomColor(),
            })
        }

        content.forEach((element) => {
            datasets.push({
                label: element.title,
                data: labels.map((label) => {
                    const data = element.dataList.find((item) => moment(item.date).format('DD.MM.YYYY') === label.date)

                    return getGraphValue(data)
                }),
                borderColor: labels.map((label) => label.borderColor),
                backgroundColor: labels.map((label) => label.backgroundColor),
                borderWidth: 1,
            })
        })

        setDoughnutGraphData({
            labels: labels.map((label) => label.date),
            datasets,
        })
    }
    const renderGraph = () => {
        switch (graphType) {
            case 'bars':
                return <BarGraph data={graphData} isMobile={isMobile} />
            case 'lines':
                return <LineGraph data={graphData} isMobile={isMobile} />
            case 'doughnuts':
                return <DoughnutGraph data={doughnutGraphData} isMobile={isMobile} />
            default:
                return null
        }
    }

    function getRandomColor() {
        const letters = '0123456789ABCDEF'
        let color = '#'
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)]
        }
        return color
    }

    const checkMobile = () => {
        setIsMobile(window.innerWidth < 700)
    }

    useEffect(() => {
        convertDataToGraph(selectedItem)
        convertDataToDoughnutGraph(selectedItem)
    }, [report, contentKey, selectedItem, graphType])
    useEffect(() => {
        api.send<{ id: number }, IReportPreview>('reportPreview', { id: selectedInfo.id }).then((res) => {
            setReport(res)
        })
    }, [])
    useEffect(() => {
        if (report?.digitalSignages.length) {
            setSelectedItem(report.digitalSignages[0].id)
        }
    }, [report])
    useEffect(() => {
        checkMobile()
        window.addEventListener('resize', checkMobile)

        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    return (
        <div ref={wrapperRef} className={styles.wrapper}>
            {report && checkAvailableItems() ? (
                <>
                    {isMobile && (
                        <div className={styles.toolbarsTriggers}>
                            <div className={styles.panelTrigger} onClick={() => setHideLeftPanel(!hideLeftPanel)}>
                                <Icon type={hideLeftPanel ? 'display' : 'close'} size={16} />
                            </div>
                            <div className={styles.panelTrigger} onClick={() => setHideRightPanel(!hideRightPanel)}>
                                <Icon type={hideRightPanel ? 'gear' : 'close'} size={16} />
                            </div>
                        </div>
                    )}
                    <div className={cn(styles.leftBlock, isMobile && !hideLeftPanel ? styles.leftBlockOpened : '')}>
                        <div className={styles.leftBlockTitle}>{itemsType}:</div>
                        <div className={styles.leftBlockList}>
                            {getItemsTabs(report.digitalSignages).map((item) => {
                                return (
                                    <div
                                        className={cn(
                                            styles.listItem,
                                            selectedItem === item.id ? styles.selectedItem : ''
                                        )}
                                        key={item.id}
                                        onClick={() => setSelectedItem(item.id)}
                                    >
                                        <Icon type={'display'} size={16} />
                                        <span>{item.name}</span>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                    <div className={styles.centerBlock}>
                        <Steps className={styles.tabs} active={graphType}>
                            {graphTypes
                                .map((type, index) => {
                                    return (
                                        <button id={type} key={type + index} onClick={() => setGraphType(type)}>
                                            {translate(type)}
                                        </button>
                                    )
                                })
                                .filter((step) => step)}
                        </Steps>
                        {graphData && <div className={styles.chartWrapper}>{renderGraph()}</div>}
                    </div>
                    <div className={cn(styles.rightBlock, isMobile && !hideRightPanel ? styles.rightBlockOpened : '')}>
                        <div className={styles.RightBlockTitle}>{translate('showByParameter')}:</div>
                        <div className={styles.paramsList}>
                            {params &&
                                params.map((param, index) => (
                                    <div
                                        key={param + index}
                                        onClick={() => setContentKey(param)}
                                        className={cn(styles.param, param === contentKey ? styles.selectedParam : '')}
                                    >
                                        {translate(`report_parameter_${param}`)}
                                    </div>
                                ))}
                        </div>
                    </div>
                </>
            ) : (
                <div className={styles.noData}>{translate('noDataToView')}</div>
            )}
        </div>
    )
}

export default ReportChart
